/**
 * Data access for the four accounting reports (spec §7.7–§7.8). The shapes
 * mirror `modules/CORE/domain/ledger/ledger.types.ts`: money is a decimal
 * string on the wire (AD-8) and never becomes a JavaScript number here.
 */

import type { Language } from '../../i18n/catalogs';
import type { JsonFetch } from '../accounts/accounts.api';
import { reportQuery } from './periods';

export type { JsonFetch } from '../accounts/accounts.api';

export const DIM_SLOTS = ['dim1', 'dim2', 'dim3', 'dim4', 'dim5', 'dim6', 'dim7', 'dim8'] as const;

export type DimSlot = (typeof DIM_SLOTS)[number];

export interface ReportPeriod {
  id: string;
  orgId: string;
  year: number;
  month: number;
  status: 'open' | 'closing' | 'closed';
}

export interface SideTotals {
  openingDt: string;
  openingCt: string;
  turnoverDt: string;
  turnoverCt: string;
  closingDt: string;
  closingCt: string;
}

export interface TrialBalanceRow extends SideTotals {
  accountCode: string;
  nameAz: string;
  nameEn: string;
  nameRu: string;
  isGroup: boolean;
  isOffbalance: boolean;
  level: number;
  closing: string;
}

export interface TrialBalanceReport {
  from: string;
  to: string;
  rows: TrialBalanceRow[];
  totals: SideTotals;
  offBalanceTotals: SideTotals;
}

export interface AccountCardRow {
  entryId: string;
  lineId: string;
  date: string;
  documentId: string;
  documentType: string;
  description: string | null;
  debit: string;
  credit: string;
  balance: string;
  currency: string;
  amountCur: string;
  quantity: string | null;
  dims: Partial<Record<DimSlot, string>>;
}

export interface AccountCardReport {
  account: string;
  from: string;
  to: string;
  opening: string;
  rows: AccountCardRow[];
  turnoverDt: string;
  turnoverCt: string;
  closing: string;
}

export interface AccountAnalysisGroup {
  dimension: DimSlot;
  value: string | null;
  opening: string;
  turnoverDt: string;
  turnoverCt: string;
  closing: string;
  lineCount: number;
}

export interface AccountAnalysisReport {
  account: string;
  from: string;
  to: string;
  by: DimSlot;
  opening: string;
  groups: AccountAnalysisGroup[];
  totals: { opening: string; turnoverDt: string; turnoverCt: string; closing: string };
}

export interface ChessTableCell {
  accountCode: string;
  amount: string;
}

export interface ChessTableRow {
  accountCode: string;
  cells: ChessTableCell[];
  total: string;
}

export interface ChessTableColumn {
  accountCode: string;
  total: string;
}

export interface ChessTableReport {
  from: string;
  to: string;
  rows: ChessTableRow[];
  columns: ChessTableColumn[];
  grandTotal: string;
}

export interface NamedAccountRow {
  nameAz: string;
  nameEn: string;
  nameRu: string;
}

export function localizedName(row: NamedAccountRow, language: Language): string {
  if (language === 'en') {
    return row.nameEn;
  }
  if (language === 'ru') {
    return row.nameRu;
  }
  return row.nameAz;
}

export interface RangeFilter {
  from?: string;
  to?: string;
  orgId?: string;
}

export interface AccountFilter extends RangeFilter {
  account?: string;
}

export interface AnalysisFilter extends AccountFilter {
  by?: DimSlot;
}

export async function loadReportPeriods(fetchJson: JsonFetch): Promise<ReportPeriod[]> {
  return fetchJson<ReportPeriod[]>('/periods');
}

export async function loadTrialBalance(
  fetchJson: JsonFetch,
  filter: RangeFilter,
): Promise<TrialBalanceReport> {
  return fetchJson<TrialBalanceReport>(`/reports/trial-balance?${reportQuery({ ...filter })}`);
}

export async function loadAccountCard(
  fetchJson: JsonFetch,
  filter: AccountFilter,
): Promise<AccountCardReport> {
  return fetchJson<AccountCardReport>(`/reports/account-card?${reportQuery({ ...filter })}`);
}

export async function loadAccountAnalysis(
  fetchJson: JsonFetch,
  filter: AnalysisFilter,
): Promise<AccountAnalysisReport> {
  return fetchJson<AccountAnalysisReport>(`/reports/account-analysis?${reportQuery({ ...filter })}`);
}

export async function loadChessTable(
  fetchJson: JsonFetch,
  filter: RangeFilter,
): Promise<ChessTableReport> {
  return fetchJson<ChessTableReport>(`/reports/chess-table?${reportQuery({ ...filter })}`);
}
